// ─────────────────────────────────────────────────────────────────────────────
// WP-15 REQ-126 — Admin del Programa de Reconocimiento (reescritura del iframe legacy de
// ~5.809 líneas), hoy con PARIDAD DEMO en su navegación.
//
// El demo no tiene una página de admin aparte: sus 12 módulos son un submenú del sidebar del
// Panel Admin (index.html : 307-312 + fundamentales-admin.js : 1402-1411). Por eso esta vista
// dejó de dibujar su propio shell con tabs horizontales y ahora RENDERIZA EL PANEL ADMIN con el
// bloque "Fundamentales" abierto en la sección pedida: una sola pantalla de administración,
// igual que el demo.
//
// La ruta /app/admin/<slug> se conserva (deep-links, entrada "Panel Admin" del menú Más en
// frontline, e2e visual) — ver CONTRATO-RUTAS.md. El registro de secciones y su render viven en
// `sections.tsx`, que es lo que consume el panel.
//
// P4-2/P4-4 (Fase 4): lo que queda aquí es SOLO enrutamiento — validar el slug y pasárselo al
// panel. El gating de cada sección lo resuelve el panel con `getAccessibleRecognitionSections`;
// un slug válido pero sin permiso abre el panel en la primera pestaña que el usuario sí tenga,
// nunca en la sección pedida. La autorización real la manda el backend.
// ─────────────────────────────────────────────────────────────────────────────
import { useParams, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AdminView as AdminPanelView } from '@/views/AdminView';
import { canAccessView } from '@/contexts/UserContext';
import { useVisibleNavLinks } from '@/hooks/useVisibleNavLinks';
import { ADMIN_SECTIONS, ALL_ADMIN_SLUGS } from './sections';
import '../recognition.css';

export { ADMIN_SECTIONS } from './sections';

export function AdminView() {
  const { t } = useTranslation('recognition');
  const params = useParams();
  // `activeRoles` (no `useEffectiveRoles`) para que el guard use EXACTAMENTE los mismos inputs que
  // la visibilidad de la entrada "Administración" del Sidebar (`isViewVisible('admin')`), todos de
  // una sola instancia de `useCurrentUser`: si el Sidebar muestra la entrada, este guard deja pasar.
  const { activeRoles, permissions, modules, recognitionPermissions, profileResolved } = useVisibleNavLinks();
  const section = params.section ?? ADMIN_SECTIONS[0].slug;

  if (!ALL_ADMIN_SLUGS.includes(section)) {
    return <Navigate to={`/app/admin/${ADMIN_SECTIONS[0].slug}`} replace />;
  }

  // NO decidir acceso mientras el perfil autenticado aún resuelve. `useCurrentUser` refetchea el
  // perfil por cada montaje, así que al navegar a `/app/admin` esta vista arranca con los roles en
  // su fallback (Employee) durante una ventana de carga; sin esta espera, el redirect de abajo
  // botaba a un admin legítimo de vuelta a `/app/reconocer` ANTES de que su perfil cargara, y el
  // rebote (redirect de una sola vez) no se recuperaba al resolver. Era el bug de QA "de Recognize
  // a Administración se queda cargada Recognize" (en local no pasa: sin auth, `currentUser` es null
  // y `profileResolved` es true desde el inicio). Mismo criterio que LandingDecision/AdminCampaigns.
  if (!profileResolved) {
    return (
      <div className="acc-loading" role="status">
        <i className="fas fa-spinner fa-spin" aria-hidden="true" /> {t('common.loading', 'Cargando…')}
      </div>
    );
  }

  // Esta zona no pasa por `ViewPanel`, que es quien deja sin montar las vistas sin acceso en el
  // portal. Sin esta comprobación, un rol sin administración veía el armazón del panel vacío en
  // vez de no llegar: mismo criterio que el portal, resuelto por el mismo predicado.
  const hasAdminAccess = canAccessView('admin', activeRoles, permissions, modules, recognitionPermissions);
  if (!hasAdminAccess) {
    return <Navigate to="/app/reconocer" replace />;
  }

  return <AdminPanelView initialSection={section} />;
}

export default AdminView;
